import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoIcon from "@mui/icons-material/Info";
import PhoneIcon from "@mui/icons-material/Phone";
import EventIcon from "@mui/icons-material/Event";
import MailIcon from "@mui/icons-material/Mail";
import MessageIcon from "@mui/icons-material/Mail";
import VideocamIcon from "@mui/icons-material/Videocam";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import FilterListIcon from "@mui/icons-material/FilterList";
import { apiRequest, managerAPI } from "../../../api/apiHelper";

const riskColor = (score) => {
  if(score===null|| score===undefined)return "#9e9e9e"
  if (score >= 70) return "#ea504eff";
  if (score >= 40) return "#f6c54aff";
  return "#87db8bff";
};

// Stage color mapping based on stage name/number - Original dummy data style
const getStageColor = (stageName) => {
  if (!stageName) return { bgcolor: "#f8f9fa", color: "#495057", borderColor: "#e9ecef" };

  const stage = stageName.toLowerCase();

  // Original dummy data styling
  if (stage.includes("closed won") || stage.includes("won")) {
    return {
      bgcolor: "#e8f5e8",
      color: "#2e7d32",
      borderColor: "#c8e6c9"
    };
  }

  if (stage.includes("negotiation") || stage.includes("stage 4") || stage.includes("stage4")) {
    return {
      bgcolor: "#fff3cd",
      color: "#856404",
      borderColor: "#ffeaa7"
    };
  }

  if (stage.includes("proposal") || stage.includes("stage 3") || stage.includes("stage3")) {
    return {
      bgcolor: "#d4edda",
      color: "#155724",
      borderColor: "#c3e6cb"
    };
  }

  if (stage.includes("qualification") || stage.includes("stage 2") || stage.includes("stage2")) {
    return {
      bgcolor: "#cce7ff",
      color: "#004085",
      borderColor: "#bee5eb"
    };
  }

  if (stage.includes("discovery") || stage.includes("stage 1") || stage.includes("stage1")) {
    return {
      bgcolor: "#f8f9fa",
      color: "#495057",
      borderColor: "#e9ecef"
    };
  }

  // Closed Lost - Error red
  if (stage.includes("closed lost") || stage.includes("lost") || stage.includes("rejected")) {
    return {
      bgcolor: "#ffebee",
      color: "#d32f2f",
      borderColor: "#ffcdd2"
    };
  }

  // Default - Grey theme
  return {
    bgcolor: "#f8f9fa",
    color: "#495057",
    borderColor: "#e9ecef"
  };
};


function TabPanel(props) {
  const { children, value, index, ...other } = props;


  return (
    <div
      role="tabpanel"
      hidden={ value !== index }
      id={ `tabpanel-${index}` }
      aria-labelledby={ `tab-${index}` }
      { ...other }
    >
      { value === index && <Box sx={ { pt: 3, pl: 0, pr: 0, pb: 0 } }>{ children }</Box> }
    </div>
  );
}

const Insights = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [meddicData, setMeddicData] = useState([]);
  const [dealsData, setDealsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);
  const [convData, setConvData] = useState(null);
  const handleTabChange = (e, newValue) => setTab(newValue);
  const { id } = JSON.parse(localStorage.getItem("userData"));


  // Fetch deals data on component mount
  useEffect(() => {
    fetchDealsData();
  }, []);

  // Fetch conversations when user navigates to Conversations tab
  useEffect(() => {
    if (tab === 1) {
      fetchConversationsData();
    }
  }, [tab]);

  const fetchConversationsData = async () => {
    setConvLoading(true);
    setConvError(null);
    try {
      const resp = await managerAPI.getConversationsOverview();
      // apiRequest returns the inner data object
      const data = resp?.data || resp || null;
      setConvData(data);
    } catch (err) {
      setConvError(err.message || "Failed to fetch conversations overview");
      setConvData(null);
    } finally {
      setConvLoading(false);
    }
  };

  const fetchDealsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest('get', '/insights/deals');

      // Check multiple possible response structures
      let dealData = null;
      if (response?.data?.data) {
        dealData = response.data.data;
      } else if (response?.data) {
        dealData = response.data;
      } else if (response) {
        dealData = response;
      }



      if (dealData && Array.isArray(dealData)) {
        // Transform the API data to match the frontend expectations
        const transformedData = dealData.map(deal => ({
          ...deal,
          // Map API fields to frontend expected fields
          deal: deal.deal || deal.dealName || 'Unnamed Deal',
          stage: deal.stage || 'Unknown Stage',
          amount: deal.amount || '$0',
          company: deal.company || 'No Company',
          contactNumbers: deal.contactNumbers || [],
          next: deal.next || '-', // Use backend-processed next steps
          nextActivity: deal.nextActivity || '-', // Handle next activity date with fallback
          risk: deal.risk || 0, // Use backend-processed risk score
          detailedInsights: deal.detailedInsights || null, // Add detailed insights
          interactionHistory: deal.interactionHistory || [],
          totalInteractions: deal.totalInteractions || 0,
          eventsWithAnalysis: deal.eventsWithAnalysis || [],
          // Use backend-calculated MEDDIC data
          meddic: deal.meddic || null,
          meddicScore: deal.meddicScore || 0,
          // Keep phase responses for further processing if needed
          phase1Response: deal.phase1Response,
          phase2Response: deal.phase2Response,
          phase3Response: deal.phase3Response
        }));



        setDealsData(transformedData);


        // Extract MEDDIC data for the separate MEDDIC state from transformed deals
        const meddicExtracted = transformedData
          .filter(deal => deal.meddic && Object.keys(deal.meddic).length > 0)
          .map(deal => ({
            dealName: deal.deal,
            dealId: deal.dealId,
            ...deal.meddic
          }));

        setMeddicData(meddicExtracted);

      } else {
        setDealsData([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch deals data');
      setDealsData([]);
    } finally {
      setLoading(false);
    }
  };





  // Map of MEDDIC criteria and their letter labels 
  const meddiccCriteria = [
    { key: 'metrics', label: 'M', fullName: 'Metrics' },
    { key: 'economicBuyer', label: 'E', fullName: 'Economic Buyer' },
    { key: 'decisionCriteria', label: 'D', fullName: 'Decision Criteria' },
    { key: 'decisionProcess', label: 'D', fullName: 'Decision Process' },
    { key: 'identifyPain', label: 'I', fullName: 'Identify Pain' },
    { key: 'champion', label: 'C', fullName: 'Champion' },
  ];

  // Find MEDDIC entry for a given revenue deal name
  const getMeddicForDeal = (dealName, dealData = null) => {
    // First, try to get MEDDIC from the deal data itself
    if (dealData && dealData.meddic) {
      return { ...dealData.meddic, dealName };
    }

    // Fallback to separate MEDDIC data
    if (!meddicData || !dealName) {
      return null;
    }

    const lowerDeal = dealName.toLowerCase();
    const found = meddicData.find((m) => {
      if (!m.dealName) return false;
      const d = m.dealName.toLowerCase();
      return d === lowerDeal || d.includes(lowerDeal) || lowerDeal.includes(d);
    }) || null;

    return found;
  };


  // Calculate revenue summary from deals data
  const revenueSummary = React.useMemo(() => {
    if (!dealsData || dealsData.length === 0) {
      return [];
    }

    const openDeals = dealsData.filter(deal =>
      deal.dealStatus === 'open' || (!deal.dealStatus && deal.stage && !deal.stage.toLowerCase().includes('closed'))
    );
    const closedWonDeals = dealsData.filter(deal =>
      deal.dealStatus === 'closed_won'
    );
    const closedLostDeals = dealsData.filter(deal =>
      deal.dealStatus === 'closed_lost'
    );

    const parseAmount = (amount) => {
      if (!amount) return 0;
      // Remove currency symbols and parse number
      const numStr = amount.replace(/[^\d.-]/g, '');
      return parseFloat(numStr) || 0;
    };

    const formatAmount = (total) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(total);
    };

    const openTotal = openDeals.reduce((sum, deal) => sum + parseAmount(deal.amount), 0);
    const closedWonTotal = closedWonDeals.reduce((sum, deal) => sum + parseAmount(deal.amount), 0);
    const closedLostTotal = closedLostDeals.reduce((sum, deal) => sum + parseAmount(deal.amount), 0);

    return [
      { label: "Open Deals", value: formatAmount(openTotal), count: openDeals.length },
      { label: "Closed Won", value: formatAmount(closedWonTotal), count: closedWonDeals.length },
      { label: "Closed Lost", value: formatAmount(closedLostTotal), count: closedLostDeals.length },
    ];
  }, [dealsData]);

  // Conversation summary calculations (component scope)
  const totalConversations = convData?.totals?.events || 0;
  const userCount = convData?.totals?.users || (convData?.users?.length || 0);
  const avgConversationsPerUser = userCount > 0 ? Math.round(totalConversations / userCount) : 0;
  const totalMs = (convData?.users || []).reduce((s, u) => s + (u.totalDurationMs || 0), 0);
  const totalHours = Math.round((totalMs / 3600000) * 10) / 10; // one decimal
  const avgTimePerUser = userCount > 0 ? Math.round((totalHours / userCount) * 10) / 10 : 0;

  const convUsersRows = (convData?.users || []).map((u) => {
    const totalInteractions = u.totalInteractions || ((u.createdCount || 0) + (u.attendeeCount || 0));
    const hours = Math.round(((u.totalDurationMs || 0) / 3600000) * 10) / 10;
    const avgMin = totalInteractions ? Math.round(((u.totalDurationMs || 0) / (totalInteractions || 1)) / 60000) : '-';

    return (
      <Box key={ u.userId || u.email } sx={ { display: 'flex', alignItems: 'center', py: 1, borderBottom: '1px solid #f1f1f1' } }>
        <Box sx={ { flex: 2, display: 'flex', alignItems: 'center', gap: 1, pl: 2 } }>
          <Avatar sx={ { width: 34, height: 34 } }>{ (u.name || u.email || 'U').charAt(0).toUpperCase() }</Avatar>
          <Box>
            <Typography variant="body2" sx={ { fontWeight: 600 } }>{ u.name || u.email }</Typography>
            <Typography variant="caption" color="text.secondary">{ u.role || '' }</Typography>
          </Box>
        </Box>
        <Box sx={ { flex: 2, textAlign: 'center' } }>{ totalInteractions }</Box>
        <Box sx={ { flex: 2, textAlign: 'center' } }>{ hours } hr</Box>
        <Box sx={ { flex: 1, textAlign: 'center' } }>{ typeof avgMin === 'number' ? `${avgMin} min` : '-' }</Box>
      </Box>
    );
  });

  return (
    <Box sx={ { width: "100%", marginTop: "0px" } }>
      <Tabs value={ tab } onChange={ handleTabChange } aria-label="insights tabs">
        <Tab label="Revenue Insights" />
        <Tab label="Conversations Insights" />
      </Tabs>
      <TabPanel value={ tab } index={ 0 }>
        <Typography variant="h5" sx={ { fontSize: 20, fontWeight: 600, pb: 2 } }>Revenue Insights</Typography>
        {/* Summary Cards */ }
        <Box sx={ { display: "flex", gap: 2, mb: 2, flexWrap: "wrap" } }>
          { revenueSummary.map((item) => (
            <Card
              key={ item.label }
              sx={ {
                minWidth: 160,
                flex: '1 1 160px',
                bgcolor: "white",
                boxShadow: 1,
                borderRadius: 2,
                borderTop: "1px solid rgba(134, 136, 138, 1)",
              } }
            >
              <CardContent>
                <Typography color="text.secondary" fontSize={ 16 }>
                  { item.label }
                </Typography>
                <Typography variant="h5" color="#003366" sx={{ wordBreak: 'break-all', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                  { item.value }{ " " }
                  <span
                    style={ { fontSize: 18, color: "#888" } }
                  >{ `(${item.count})` }</span>
                </Typography>
              </CardContent>
            </Card>
          )) }
        </Box>
        {/* Filters/Chips */ }
        {/* <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {revenueFilters.map((filter) => (
            <Chip
              key={filter}
              label={filter}
              color={filter === "This Quarter" ? "primary" : "default"}
              variant={filter === "This Quarter" ? "filled" : "outlined"}
            />
          ))}
        </Box> */}
        {/* Data Table */ }
        { loading && (
          <Box sx={ { display: 'flex', justifyContent: 'center', p: 3 } }>
            <CircularProgress />
          </Box>
        ) }
        { error && (
          <Alert severity="error" sx={ { mb: 2 } }>
            { error }
          </Alert>
        ) }
        { !loading && !error && (
          <>

            <TableContainer component={ Card } sx={ { boxShadow: 1, borderRadius: 2, width: '100%', p: 0, overflowX: 'auto' } }>
              <Table size="medium" sx={ { width: '100%', minWidth: 900 } }>
                <TableHead>
                  <TableRow>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16 } }>Deal Name</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16 } }>Contact Person</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16 } }>Call Score</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16 } }>Risk Score</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16 } }>Stage</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16, textAlign: 'center' } }>
                      <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 } }>
                        MEDDIC
                        <MuiTooltip
                          title={
                            <Box sx={ { p: 1, maxWidth: 400 } }>
                              <Typography variant="body2" sx={ { fontWeight: 'bold', mb: 1, color: 'white' } }>
                                MEDDIC Sales Methodology
                              </Typography>
                              <Typography variant="body2" sx={ { mb: 2, color: 'white' } }>
                                MEDDIC is a popular sales methodology designed to help teams qualify and prioritize leads. It's a structured approach that enables you to gain a deeper understanding into your buyers, streamline lead qualification and sales process, and build effective strategies that help you close more deals.
                              </Typography>
                              <Typography variant="body2" sx={ { fontWeight: 'bold', mb: 1, color: 'white' } }>
                                MEDDIC stands for:
                              </Typography>
                              <Typography variant="body2" sx={ { color: 'white' } }>
                                • <strong>Metrics</strong><br />
                                • <strong>Economic Buyer</strong><br />
                                • <strong>Decision Criteria</strong><br />
                                • <strong>Decision Process</strong><br />
                                • <strong>Identify Pain</strong><br />
                                • <strong>Champion</strong>
                              </Typography>
                            </Box>
                          }
                          placement="bottom-start"
                          arrow
                        >
                          <InfoIcon
                            sx={ {
                              fontSize: 18,
                              color: '#396afc',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#2c4fd4'
                              }
                            } }
                          />
                        </MuiTooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0' } }>
                      <Box sx={ { textAlign: 'center' } }>
                        <Typography variant="body2" sx={ { fontWeight: 600, fontSize: 15, mb: 1 } }>
                          Deal Interaction History
                        </Typography>
                        {/* Month Timeline in Header */ }
                        <Box sx={ { display: 'flex', justifyContent: 'space-between', px: 0, width: '100%' } }>
                          { (() => {
                            // Generate 4 months timeline based on current date
                            const currentDate = new Date();
                            const months = [];

                            // Start from 3 months ago to current month
                            for (let i = 3; i >= 0; i--) {
                              const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                              const monthLabel = monthDate.toLocaleDateString('en-US', {
                                year: '2-digit',
                                month: 'short'
                              });
                              months.push(monthLabel);
                            }

                            return months.map((month, index) => (
                              <Typography
                                key={ index }
                                variant="caption"
                                sx={ {
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  flexShrink: 0,
                                  textAlign: 'center',
                                  // 🔹 no margin for first and last, gap for middle ones only
                                  mr: index !== months.length - 1 ? 'auto' : 0,
                                } }
                              >
                                { month }
                              </Typography>
                            ));
                          })() }
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16, maxWidth: 150 } }>Next Steps</TableCell>
                    <TableCell sx={ { fontWeight: 600, borderRight: '1px solid #e0e0e0', fontSize: 16, maxWidth: 120 } }>Next Activity</TableCell>
                    <TableCell sx={ { fontWeight: 600, fontSize: 16, maxWidth: 100 } }>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  { dealsData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={ 9 } align="center" sx={ { py: 4 } }>
                        <Typography variant="body2" color="text.secondary" sx={ { fontSize: 16 } }>
                          No deals data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    dealsData.map((row, idx) => (
                      <TableRow key={ row.deal }>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0' } }>
                          <Typography
                            variant="body2"
                            onClick={ () => navigate(`/home/lead/${row.dealId || row._id || 'view'}`, { state: { deal: row, fromPage: 'insights' } }) }
                            sx={ {
                              fontSize: 16,
                              fontWeight: 'bold',
                              color: '#396afc',
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            } }
                          >
                            { row.deal }
                          </Typography>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0' } }>
                          <Box>
                            { row.contactNumbers && row.contactNumbers.length > 0 ? (
                              <Box>
                                <Typography variant="body2" sx={ { fontSize: 16, cursor: 'default', fontWeight: 700 } }>
                                  { row.contactNumbers[0].name }
                                </Typography>
                                <Typography variant="body2" sx={ { fontSize: 16, cursor: 'default', display: 'block' } }>
                                  { row.contactNumbers[0].email }
                                </Typography>
                                <Typography variant="body2" sx={ { fontSize: 16, cursor: 'default' } }>
                                  { row.contactNumbers[0].number }
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={ { fontSize: 16, cursor: 'default' } }>
                                No contact info
                              </Typography>
                            ) }
                          </Box>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0', textAlign: 'center' } }>
                          <Box sx={ { display: "flex", alignItems: "center", justifyContent: "center", gap: 1 } }>
                            <MuiTooltip
                              title={
                                <Box sx={ {
                                  p: 2,
                                  background: row.callScore >= 7
                                    ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                    : row.callScore >= 4
                                      ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                      : row.callScore
                                        ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                                        : 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)',
                                  borderRadius: 2,
                                  boxShadow: row.callScore >= 7
                                    ? '0 8px 32px rgba(76, 175, 80, 0.3)'
                                    : row.callScore >= 4
                                      ? '0 8px 32px rgba(33, 150, 243, 0.3)'
                                      : row.callScore
                                        ? '0 8px 32px rgba(255, 152, 0, 0.3)'
                                        : '0 8px 32px rgba(158, 158, 158, 0.3)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  maxWidth: 350
                                } }>
                                  <Typography variant="body2" sx={ {
                                    fontWeight: 700,
                                    color: 'white',
                                    mb: 1.5,
                                    fontSize: 15,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                  } }>
                                    { row.callScore >= 7 ? '🎯' : row.callScore >= 4 ? '📈' : row.callScore ? '📊' : '❓' }
                                    <span>Call Score: <strong>{ row.callScore || 'N/A' }</strong> / 10</span>
                                    <Chip
                                      label={ row.callScore >= 7 ? 'Excellent' : row.callScore >= 4 ? 'Good' : row.callScore ? 'Needs Improvement' : 'No Data' }
                                      size="small"
                                      sx={ {
                                        ml: 1,
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontSize: 12,
                                        height: 20
                                      } }
                                    />
                                  </Typography>

                                  <Box sx={ {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 1,
                                    p: 1.5
                                  } }>
                                    <Typography variant="caption" sx={ {
                                      color: 'rgba(255, 255, 255, 0.8)',
                                      fontWeight: 600,
                                      fontSize: 13,
                                      mb: 0.5,
                                      display: 'block'
                                    } }>
                                      Performance Rating:
                                    </Typography>
                                    <Typography variant="caption" sx={ {
                                      color: 'white',
                                      fontSize: 14,
                                      display: 'block',
                                      lineHeight: 1.4,
                                      wordBreak: 'break-word',
                                      whiteSpace: 'normal',
                                    } }>
                                      { row.callScore >= 7 ? 'Outstanding call performance with excellent communication and closing ability.' :
                                        row.callScore >= 4 ? 'Good call performance with room for improvement in some areas.' :
                                          row.callScore ? 'Call performance needs improvement. Focus on communication clarity and product explanation.' :
                                            'Call score not available for this deal.' }
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              placement="bottom"
                              arrow
                              componentsProps={ {
                                tooltip: {
                                  sx: {
                                    bgcolor: 'transparent',
                                    '& .MuiTooltip-arrow': {
                                      color: row.callScore >= 7 ? '#4caf50' : row.callScore >= 4 ? '#2196f3' : row.callScore ? '#ff9800' : '#9e9e9e',
                                    },
                                  },
                                },
                              } }
                            >
                              <Box
                                sx={ {
                                  bgcolor: row.callScore >= 7 ? '#4caf50' : row.callScore >= 4 ? '#2196f3' : row.callScore ? '#ff9800' : '#9e9e9e',
                                  color: '#ffffff',
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 14,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease-in-out',
                                  opacity: 1,
                                  filter: 'none',
                                  textShadow: 'none',
                                  fontFamily: 'inherit',
                                  '&:hover': {
                                    boxShadow: `0 4px 12px ${row.callScore >= 7 ? '#4caf50' : row.callScore >= 4 ? '#2196f3' : row.callScore ? '#ff9800' : '#9e9e9e'}50`,
                                    transform: 'scale(1.05)'
                                  }
                                } }
                              >
                                { row.callScore || 'N/A' }
                              </Box>
                            </MuiTooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0', textAlign: 'center' } }>
                          <Box sx={ { display: "flex", alignItems: "center", justifyContent: "center", gap: 1 } }>
                            <MuiTooltip
                              title={
                                <Box sx={ {
                                  p: 2,
                                  background: row.risk === null
                                    ? 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
                                    : row.risk >= 70
                                    ?'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                                    :row.risk>=40
                                      ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                                      : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                  borderRadius: 2,
                                  boxShadow:row.risk===null
                                  ? '0 8px 32px rgba(0,0,0,0.2)'
                                    : row.risk >= 70
                                    ? '0 8px 32px rgba(244, 67, 54, 0.3)'
                                    : row.risk >= 40
                                      ? '0 8px 32px rgba(255, 152, 0, 0.3)'
                                      : '0 8px 32px rgba(76, 175, 80, 0.3)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  maxWidth: 350
                                } }>
                                  <Typography variant="body2" sx={ {
                                    fontWeight: 700,
                                    color: 'white',
                                    mb: 1.5,
                                    fontSize: 15,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                  } }>
                                    { row.risk === null ? 'ℹ️' : row.risk >= 70 ? '🚨' : row.risk >= 40 ? '⚠️' : '✅' }
                                    <span>Risk Score: <strong>{ row.risk ?? 'N/A' }</strong>{ row.risk !== null ? ' / 100' : '' }</span>
                                    <Chip
                                      label={ row.risk === null ? 'No Data' : row.risk >= 70 ? 'High Risk' : row.risk >= 40 ? 'Medium Risk' : 'Low Risk' }
                                      size="small"
                                      sx={ {
                                        ml: 1,
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontSize: 12,
                                        height: 20
                                      } }
                                    />
                                  </Typography>

                                  { row.detailedInsights?.risk && (
                                    <Box sx={ {
                                      background: 'rgba(255, 255, 255, 0.1)',
                                      borderRadius: 1,
                                      p: 1.5
                                    } }>
                                      {/* Risk Factors */ }
                                      { row.detailedInsights.risk.factors && row.detailedInsights.risk.factors.length > 0 && (
                                        <Box sx={ { mb: 1.5 } }>
                                          <Typography variant="caption" sx={ {
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            mb: 0.5,
                                            display: 'block'
                                          } }>
                                            { row.risk >= 70 ? 'Critical Issues:' : row.risk >= 40 ? 'Areas of Concern:' : 'Minor Notes:' }
                                          </Typography>
                                          { row.detailedInsights.risk.factors.map((factor, idx) => {
                                            const text = typeof factor === 'string'
                                              ? factor
                                              : (factor && (factor.text || factor.value || factor.description)) || JSON.stringify(factor);
                                            return (
                                              <Typography key={ idx } variant="caption" sx={ {
                                                color: 'white',
                                                fontSize: 12,
                                                display: 'block',
                                                mb: 0.5,
                                                pl: 1
                                              } }>
                                                • { text }
                                              </Typography>
                                            );
                                          }) }
                                        </Box>
                                      ) }

                                      {/* Mitigation Suggestions */ }
                                      { row.detailedInsights.risk.mitigation && row.detailedInsights.risk.mitigation.length > 0 && (
                                        <Box>
                                          <Typography variant="caption" sx={ {
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            fontWeight: 600,
                                            fontSize: 15,
                                            mb: 0.5,
                                            display: 'block'
                                          } }>
                                            { row.risk >= 70 ? 'Urgent Actions:' : row.risk >= 40 ? 'Recommended Actions:' : 'Improvement Suggestions:' }
                                          </Typography>
                                          { row.detailedInsights.risk.mitigation.map((action, idx) => {
                                            const text = typeof action === 'string'
                                              ? action
                                              : (action && (action.text || action.value || action.description)) || JSON.stringify(action);
                                            return (
                                              <Typography key={ idx } variant="caption" sx={ {
                                                color: 'white',
                                                fontSize: 12,
                                                display: 'block',
                                                mb: 0.5,
                                                pl: 1
                                              } }>
                                                • { text }
                                              </Typography>
                                            );
                                          }) }
                                        </Box>
                                      ) }

                                      {/* Show message if no detailed info */ }
                                      { (!row.detailedInsights.risk.factors || row.detailedInsights.risk.factors.length === 0) &&
                                        (!row.detailedInsights.risk.mitigation || row.detailedInsights.risk.mitigation.length === 0) && (
                                          <Typography variant="caption" sx={ {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: 14,
                                            fontStyle: 'italic'
                                          } }>
                                            { row.risk >= 70 ? 'High risk detected but no detailed analysis available' :
                                              row.risk >= 40 ? 'Medium risk level - monitor closely' :
                                                'Low risk - deal is in good shape' }
                                          </Typography>
                                        ) }
                                    </Box>
                                  ) }
                                </Box>
                              }
                              placement="bottom"
                              arrow
                              componentsProps={ {
                                tooltip: {
                                  sx: {
                                    bgcolor: 'transparent',
                                    '& .MuiTooltip-arrow': {
                                      color: row.risk >= 70 ? '#f44336' : row.risk >= 40 ? '#ff9800' : '#4caf50',
                                    },
                                  },
                                },
                              } }
                            >
                              <Box
                                sx={ {
                                  bgcolor: riskColor(row.risk),
                                  color: '#ffffff',
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 14,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease-in-out',
                                  opacity: 1,
                                  filter: 'none',
                                  textShadow: 'none',
                                  fontFamily: 'inherit',
                                  '&:hover': {
                                    boxShadow: `0 4px 12px ${riskColor(row.risk)}50`,
                                    transform: 'scale(1.05)'
                                  }
                                } }
                              >
                                { row.risk ?? 'N/A'}
                              </Box>
                            </MuiTooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0' } }>
                          <Box
                            sx={ {
                              px: 1,
                              py: 0.5,
                              bgcolor: getStageColor(row.stage).bgcolor,
                              color: getStageColor(row.stage).color,
                              borderRadius: 1,
                              fontSize: 16,
                              fontWeight: 500,
                              textAlign: "center",
                              border: "1px solid",
                              borderColor: getStageColor(row.stage).borderColor,
                              cursor: 'default',
                            } }
                          >
                            { row.stage }
                          </Box>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0' } }>
                          {/* MEDDIC letter boxes: show all letters, highlight only when true */ }
                          <Box sx={ { display: 'flex', gap: 1.8, alignItems: 'center', justifyContent: 'center', width: '100%' } }>
                            { meddiccCriteria.map((c, i) => {
                              const med = getMeddicForDeal(row.deal, row);

                              // Handle different MEDDIC data structures
                              let isHighlighted = false;
                              if (med) {
                                // All criteria are good when TRUE or present
                                isHighlighted = med[c.key] === true || med[c.key]?.present === true;
                              }

                              return (
                                <MuiTooltip
                                  title={
                                    <Box sx={ {
                                      p: 2,
                                      background: isHighlighted
                                        ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                        : 'linear-gradient(135deg, #9e9e9e 0%, #616161 100%)',
                                      borderRadius: 2,
                                      boxShadow: isHighlighted
                                        ? '0 8px 32px rgba(76, 175, 80, 0.3)'
                                        : '0 8px 32px rgba(158, 158, 158, 0.3)',
                                      border: '1px solid rgba(255, 255, 255, 0.2)',
                                      maxWidth: 300
                                    } }>
                                      <Typography variant="body2" sx={ {
                                        fontWeight: 700,
                                        color: 'white',
                                        mb: 1.5,
                                        fontSize: 18,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                      } }>
                                        { isHighlighted ? '✅' : '❌' } { c.fullName || c.key.charAt(0).toUpperCase() + c.key.slice(1) }
                                        <Chip
                                          label={ isHighlighted ? 'Present' : 'Missing' }
                                          size="small"
                                          sx={ {
                                            ml: 1,
                                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            color: 'white',
                                            fontSize: 12,
                                            height: 20
                                          } }
                                        />
                                      </Typography>

                                      { row.detailedInsights?.meddicReasons?.[c.key] && (
                                        <Box sx={ {
                                          background: 'rgba(255, 255, 255, 0.1)',
                                          borderRadius: 1,
                                          p: 1.5
                                        } }>
                                          <Typography variant="caption" sx={ {
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            fontWeight: 600,
                                            fontSize: 13,
                                            mb: 1,
                                            display: 'block'
                                          } }>
                                            Details:
                                          </Typography>
                                          <Typography variant="caption" sx={ {
                                            color: 'white',
                                            fontSize: 14,
                                            display: 'block',
                                            lineHeight: 1.4
                                          } }>
                                            { row.detailedInsights.meddicReasons[c.key].value || 'No specific details available' }
                                          </Typography>

                                          { row.detailedInsights.meddicReasons[c.key].present !== undefined && (
                                            <Typography variant="caption" sx={ {
                                              color: 'rgba(255, 255, 255, 0.7)',
                                              fontSize: 13,
                                              display: 'block',
                                              mt: 0.5,
                                              fontStyle: 'italic'
                                            } }>
                                              Status: { row.detailedInsights.meddicReasons[c.key].present ? 'Identified' : 'Not Identified' }
                                            </Typography>
                                          ) }
                                        </Box>
                                      ) }

                                      { !row.detailedInsights?.meddicReasons?.[c.key] && (
                                        <Typography variant="caption" sx={ {
                                          color: 'rgba(255, 255, 255, 0.7)',
                                          fontSize: 14,
                                          fontStyle: 'italic'
                                        } }>
                                          { isHighlighted
                                            ? 'This MEDDIC criterion has been identified but detailed analysis is not available'
                                            : 'This MEDDIC criterion needs attention to improve deal qualification'
                                          }
                                        </Typography>
                                      ) }
                                    </Box>
                                  }
                                  placement="top"
                                  arrow
                                  componentsProps={ {
                                    tooltip: {
                                      sx: {
                                        bgcolor: 'transparent',
                                        '& .MuiTooltip-arrow': {
                                          color: isHighlighted ? '#4caf50' : '#9e9e9e',
                                        },
                                      },
                                    },
                                  } }
                                >
                                  <Box
                                    key={ i }
                                    sx={ {
                                      width: 32,
                                      height: 32,
                                      minWidth: 32,
                                      minHeight: 32,
                                      bgcolor: isHighlighted ? '#22c55e' : '#e5e7eb',
                                      color: isHighlighted ? '#ffffff' : '#6b7280',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 1,
                                      fontSize: 16,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease-in-out',
                                      flexShrink: 0,
                                      boxSizing: 'border-box',
                                      opacity: 1,
                                      filter: 'none',
                                      textShadow: 'none',
                                      '&:hover': {
                                        boxShadow: `0 4px 12px ${isHighlighted ? '#22c55e' : '#e5e7eb'}50`,
                                        transform: 'scale(1.05)'
                                      }
                                    } }
                                  >
                                    { c.label }
                                  </Box>
                                </MuiTooltip>
                              );
                            }) }
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={ {
                            borderRight: '1px solid #e0e0e0',
                            padding: 1,
                            minWidth: 320,
                            textAlign: 'center',
                          } }
                        >
                          {/* Timeline Component */ }  
                          { (() => {
                            // START OF NEW LOGIC
                            const today = new Date();
                            const startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
                            const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                            const totalTime = endDate.getTime() - startDate.getTime();

                            const allItems = (row.interactionHistory || []).flatMap(m => m.items || [])
                              .filter(item => {
                                const d = new Date(item.date);
                                return d >= startDate && d <= endDate;
                              })
                              .sort((a, b) => new Date(a.date) - new Date(b.date));

                            const clusters = [];
                            const THRESHOLD = 3 * 24 * 60 * 60 * 1000;

                            allItems.forEach(item => {
                              const itemTime = new Date(item.date).getTime();
                              if (clusters.length > 0) {
                                const lastCluster = clusters[clusters.length - 1];
                                const lastTime = new Date(lastCluster.items[lastCluster.items.length - 1].date).getTime();
                                if (itemTime - lastTime < THRESHOLD) {
                                  lastCluster.items.push(item);
                                  return;
                                }
                              }
                              clusters.push({ items: [item], startTime: itemTime });
                            });
                            // END OF NEW LOGIC


                            const getIcon = (type) => {
                              const t = (type || '').toLowerCase();
                              if (t.includes('google')) return <VideocamIcon sx={ { fontSize: 20, color: '#4285f4' } } />;
                              if (t.includes('video') || t.includes('meet')) return <VideocamIcon sx={ { fontSize: 20, color: '#4285f4' } } />;
                              if (t.includes('mail') || t.includes('gmail')) return <MailIcon sx={ { fontSize: 20, color: '#ea4335' } } />;
                              if (t.includes('whatsapp')) return <WhatsAppIcon sx={ { fontSize: 20, color: '#25D366' } } />;
                              if (t.includes('message') || t.includes('sms')) return <MessageIcon sx={ { fontSize: 20, color: '#7b1fa2' } } />;
                              if (t.includes('call') || t.includes('phone')) return <PhoneIcon sx={ { fontSize: 20, color: '#2e7d32' } } />;
                              if (t.includes('person') || t.includes('visit')) return <PersonIcon sx={ { fontSize: 20, color: '#ff9800' } } />;
                              return <EventIcon sx={ { fontSize: 20, color: '#666' } } />;
                            };

                            const getClusterIcon = (clusterItems) => {
                              const types = [...new Set(clusterItems.map(i => i.type))];
                              if (types.length === 1) {
                                return getIcon(types[0]);
                              }
                              return <StarIcon sx={ { fontSize: 20, color: '#f59e0b' } } />;
                            };

                            return (
                              <Box sx={ { position: 'relative', width: '100%', minWidth: 300, margin: '0 auto', minHeight: 40, display: 'flex', alignItems: 'center' } }>
                                {/* Timeline line */ }
                                <Box
                                  sx={ {
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: 0,
                                    right: 0,
                                    height: 2,
                                    background: 'linear-gradient(90deg, #e1bee7, #bbdefb, #e1bee7)',
                                    borderRadius: 1,
                                  } }
                                />

                                {/* Clusters */ }
                                <Box sx={ { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 } }>
                                  { clusters.map((cluster, idx) => {
                                    const clusterTime = cluster.startTime;
                                    let percent = ((clusterTime - startDate.getTime()) / totalTime) * 100;
                                    percent = Math.max(0, Math.min(100, percent));
                                    const count = cluster.items.length;

                                    return (
                                      <Box
                                        key={ idx }
                                        sx={ {
                                          position: 'absolute',
                                          left: `${percent}%`,
                                          transform: 'translateX(-50%)',
                                          top: '50%',
                                          marginTop: '-16px', // Half of height
                                          zIndex: 10
                                        } }
                                      >
                                        <MuiTooltip
                                          title={
                                            <Box sx={ {
                                              p: 1,
                                              maxHeight: 300,
                                              overflowY: 'auto',
                                              scrollbarWidth: 'none', // Firefox
                                              '&::-webkit-scrollbar': {
                                                display: 'none' // Chrome, Safari
                                              }
                                            } }>
                                              <Typography variant="subtitle2" sx={ { fontWeight: 600, mb: 1, color: '#fff', fontSize: 14 } }>
                                                Interaction Details ({ count })
                                              </Typography>
                                              { cluster.items.map((meeting, mIdx) => (
                                                <Box
                                                  key={ mIdx }
                                                  onClick={ (e) => {
                                                    e.stopPropagation();
                                                    const targetId = meeting.postMeeting?.meetingId || meeting.meetingId || meeting.eventId;
                                                    if (targetId) navigate(`/home/meeting/${targetId}`);
                                                  } }
                                                  sx={ {
                                                    mb: mIdx < cluster.items.length - 1 ? 1 : 0,
                                                    cursor: 'pointer',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor: 'rgba(255,255,255,0.05)',
                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                                                  } }
                                                >
                                                  <Box sx={ { display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 } }>
                                                    { getIcon(meeting.type) }
                                                    <Typography variant="body2" sx={ { color: '#fff', fontSize: '13px', fontWeight: 600 } }>
                                                      { meeting.type ? meeting.type.charAt(0).toUpperCase() + meeting.type.slice(1) : 'Meeting' }
                                                    </Typography>
                                                  </Box>

                                                  <Typography variant="body2" sx={ { color: '#e3f2fd', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 } }>
                                                    <CalendarTodayIcon sx={ { fontSize: 12, opacity: 0.8 } } />
                                                    { new Date(meeting.date).toLocaleString('en-US', {
                                                      month: 'short', day: 'numeric',
                                                      hour: '2-digit', minute: '2-digit'
                                                    }) }
                                                  </Typography>

                                                  { meeting.summary && (
                                                    <Typography variant="body2" sx={ { color: '#b0bec5', fontSize: '12px', fontStyle: 'italic' } }>
                                                      { meeting.summary.length > 50 ? `${meeting.summary.substring(0, 50)}...` : meeting.summary }
                                                    </Typography>
                                                  ) }
                                                </Box>
                                              )) }
                                              <Typography variant="caption" sx={ { color: '#90caf9', fontSize: '11px', mt: 1, display: 'block', textAlign: 'center' } }>
                                                Click item to view details
                                              </Typography>
                                            </Box>
                                          }
                                          arrow
                                          placement="top"
                                          componentsProps={ {
                                            tooltip: {
                                              sx: {
                                                bgcolor: '#263238',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                borderRadius: 2,
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                                              }
                                            }
                                          } }
                                        >
                                          <Box
                                            sx={ {
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              width: 32,
                                              height: 32,
                                              borderRadius: '50%',
                                              bgcolor: 'white',
                                              border: '1px solid #e0e0e0',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              cursor: 'pointer',
                                              position: 'relative',
                                              transition: 'transform 0.2s',
                                              '&:hover': { transform: 'scale(1.15)', zIndex: 20 }
                                            } }
                                          >
                                            { getClusterIcon(cluster.items) }

                                            {/* Count Badge for multiple items */ }
                                            { count > 1 && (
                                              <Box
                                                sx={ {
                                                  position: 'absolute',
                                                  top: -2,
                                                  right: -2,
                                                  minWidth: 16,
                                                  height: 16,
                                                  borderRadius: 8,
                                                  bgcolor: '#1976d2',
                                                  color: 'white',
                                                  fontSize: 10,
                                                  fontWeight: 700,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  px: 0.5,
                                                  border: '2px solid white',
                                                } }
                                              >
                                                { count }
                                              </Box>
                                            ) }
                                          </Box>
                                        </MuiTooltip>
                                      </Box>
                                    );
                                  }) }
                                </Box>
                              </Box>
                            );
                          })() }
                        </TableCell>
                        <TableCell
                          sx={ {
                            borderRight: '1px solid #e0e0e0',
                          } }
                        >
                          <MuiTooltip
                            title={
                              <Box sx={ {
                                p: 2,
                                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(33, 150, 243, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                maxWidth: 400
                              } }>
                                <Typography variant="body2" sx={ {
                                  fontWeight: 700,
                                  color: 'white',
                                  mb: 1.5,
                                  fontSize: 18,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                } }>
                                  📋 Next Steps
                                </Typography>

                                <Box sx={ {
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: 1,
                                  p: 1.5
                                } }>
                                  <Typography variant="body2" sx={ {
                                    color: 'white',
                                    fontSize: 15,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap'
                                  } }>
                                    { row.next || 'No next steps defined' }
                                  </Typography>
                                </Box>
                              </Box>
                            }
                            placement="top"
                            arrow
                            componentsProps={ {
                              tooltip: {
                                sx: {
                                  bgcolor: 'transparent',
                                  '& .MuiTooltip-arrow': {
                                    color: '#2196f3',
                                  },
                                },
                              },
                            } }
                          >
                            <Typography
                              variant="body2"
                              sx={ {
                                fontSize: 16,
                                cursor: 'default',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '150px'
                              } }
                            >
                              { row.next }
                            </Typography>
                          </MuiTooltip>
                        </TableCell>
                        <TableCell sx={ { borderRight: '1px solid #e0e0e0' } }>
                          <Box
                            sx={ { display: "flex", alignItems: "center", gap: 0.5, fontSize: 16, cursor: 'default' } }
                          >
                            { row.closeDir === "up" && (
                              <ArrowUpwardIcon fontSize="small" color="success" />
                            ) }
                            { row.closeDir === "down" && (
                              <ArrowDownwardIcon fontSize="small" color="error" />
                            ) }
                            { row.nextActivity || '-' }
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={ { display: "flex", alignItems: "center", gap: 0.5, fontSize: 16, cursor: 'default' } }
                          >
                            { row.amountDir === "up" && (
                              <ArrowUpwardIcon fontSize="small" color="success" />
                            ) }
                            { row.amountDir === "down" && (
                              <ArrowDownwardIcon fontSize="small" color="error" />
                            ) }
                            { row.amount }
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) }
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) }
      </TabPanel>
      <TabPanel value={ tab } index={ 1 }>
        <Typography variant="h5" sx={ { fontSize: 20, fontWeight: 600, pb: 2 } }>Conversations Overview</Typography>
        {/* Summary Cards */ }
        { convLoading ? (
          <Box sx={ { display: 'flex', justifyContent: 'center', p: 3 } }>
            <CircularProgress />
          </Box>
        ) : convError ? (
          <Box sx={ { display: 'flex', justifyContent: 'center', p: 3 } }>
            <Alert severity="error">{ convError }</Alert>
          </Box>
        ) : (
          <Box sx={ { display: "flex", gap: 3, mb: 3, flexWrap: "wrap" } }>
            <Box sx={ { flex: '1 1 180px', minWidth: 160, bgcolor: "white", p: 2, borderRadius: 2, boxShadow: 1 } }>
              <Typography color="text.secondary" fontSize={ 14 }>Total Conversations</Typography>
              <Typography variant="h4" color="#00bcd4">{ totalConversations }</Typography>
            </Box>
            <Box sx={ { flex: '1 1 180px', minWidth: 160, bgcolor: "white", p: 2, borderRadius: 2, boxShadow: 1 } }>
              <Typography color="text.secondary" fontSize={ 14 }>Average Conversations/User</Typography>
              <Typography variant="h4" color="#00bcd4">{ avgConversationsPerUser }</Typography>
            </Box>
            <Box sx={ { flex: '1 1 180px', minWidth: 160, bgcolor: "white", p: 2, borderRadius: 2, boxShadow: 1 } }>
              <Typography color="text.secondary" fontSize={ 14 }>Total Time Spent</Typography>
              <Typography variant="h4" color="#00bcd4">{ totalHours } <span style={ { fontSize: 16 } }>hr</span></Typography>
            </Box>
            <Box sx={ { flex: '1 1 180px', minWidth: 160, bgcolor: "white", p: 2, borderRadius: 2, boxShadow: 1 } }>
              <Typography color="text.secondary" fontSize={ 14 }>Average Time/User</Typography>
              <Typography variant="h4" color="#00bcd4">{ avgTimePerUser } <span style={ { fontSize: 16 } }>hr</span></Typography>
            </Box>
          </Box>
        ) }
        {/* Table */ }
        <Box sx={ { bgcolor: "white", borderRadius: 2, boxShadow: 1, p: 2, overflowX: 'auto' } }>
          <Box
            sx={ {
              display: "flex",
              fontWeight: 600,
              borderBottom: "1px solid #eee",
              pb: 1,
              mb: 1,
            } }
          >
            <Box sx={ { flex: 2, pl: 2 } }>Team Member</Box>
            <Box sx={ { flex: 2, textAlign: 'center' } }>Number of conversations</Box>
            <Box sx={ { flex: 2, textAlign: 'center' } }>Total hours spent in conversations</Box>
            <Box sx={ { flex: 1, textAlign: 'center' } }>Avg. duration</Box>
          </Box>
          { convLoading && (
            <Box sx={ { display: 'flex', justifyContent: 'center', py: 4 } }>
              <CircularProgress />
            </Box>
          ) }

          { !convLoading && convError && (
            <Box sx={ { display: 'flex', justifyContent: 'center', py: 4 } }>
              <Alert severity="error">{ convError }</Alert>
            </Box>
          ) }

          { !convLoading && !convError && convUsersRows && convUsersRows.length > 0 ? (
            convUsersRows
          ) : (!convLoading && !convError) && (
            <Box sx={ { display: 'flex', justifyContent: 'center', py: 4 } }>
              <Typography variant="body2" color="text.secondary">No conversation data available yet</Typography>
            </Box>
          ) }
        </Box>
      </TabPanel>
      <TabPanel value={ tab } index={ 2 }>
        <Typography variant="h6">Other Insights</Typography>
        <Typography>More analytics coming soon...</Typography>
      </TabPanel>
    </Box>
  );
};

export default Insights;
